<template>
    <div class="pb2 pb2--lite">
        <MountedTeleport v-if="sidebarTargetExists" to="#sidebar">
            <SidebarTabs />
        </MountedTeleport>
        <!-- hosts without the admin sidebar slot degrade to an inline palette -->
        <SidebarTabs v-else />

        <BlockPanel />

        <BuilderBar />
        <CanvasFrame />

        <CommandPalette v-model="paletteOpen" v-model:keymap="keymapOpen" />

        <BuilderContextMenu :menu="blockMenu" />

        <KeymapOverlay v-model="keymapOpen" />

        <Teleport to="body">
            <div
                v-if="drag.ghost.value"
                class="pb2-drag-ghost"
                :style="{ left: `${drag.ghost.value.x + 12}px`, top: `${drag.ghost.value.y + 12}px` }"
            >
                {{ drag.ghost.value.label }}
            </div>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import MountedTeleport from '@src/Common/Components/MountedTeleport.vue';
import { provideBuilderHost } from '@src/Common/Builder/host';
import BuilderBar from '@src/Common/Builder/BuilderBar.vue';
import BuilderContextMenu from '@src/Common/Builder/BuilderContextMenu.vue';
import CanvasFrame from '@src/Common/Builder/Canvas/CanvasFrame.vue';
import CommandPalette from '@src/Common/Builder/CommandPalette.vue';
import KeymapOverlay from '@src/Common/Builder/KeymapOverlay.vue';
import SidebarTabs from '@src/Common/Builder/Sidebar/SidebarTabs.vue';
import BlockPanel from '@src/Common/Builder/Panel/BlockPanel.vue';
import { createBuilderStore } from '@src/Common/Builder/useBuilderStore';
import { createBuilderBridge } from '@src/Common/Builder/useBuilderBridge';
import { createBuilderRegistry } from '@src/Common/Builder/useBuilderRegistry';
import { createBlockContextMenu } from '@src/Common/Builder/useBlockContextMenu';
import { createDragMachine } from '@src/Common/Builder/useDragMachine';
import { createPaletteHistory } from '@src/Common/Builder/usePaletteHistory';
import { createSidebarTab } from '@src/Common/Builder/Sidebar/useSidebarTab';
import { useBuilderShell } from '@src/Common/Builder/useBuilderShell';
import { createCanvasChrome } from '@src/Common/Builder/useCanvasChrome';
import type { BlockNode } from '@src/Common/Builder/types';
import type { BuilderHost } from '@src/Common/Builder/host';

/**
 * Canvas-lite (spec §3b/10): the builder field inside a foreign form
 * (Congress, Product). Full canvas/palette/outline/undo, but NO working
 * copy — the host form owns the data via v-model and saves it on submit.
 */
const model = defineModel<BlockNode[]>({ default: () => [] });

const props = defineProps<{ host: BuilderHost }>();

const host = props.host;
provideBuilderHost(host);

const store = createBuilderStore(model.value ?? []);
const bridge = createBuilderBridge(store);
const registry = createBuilderRegistry(host.catalog);
const paletteHistory = createPaletteHistory(host.storagePrefix);
const chrome = createCanvasChrome({ host, store, lastBlockedUrl: bridge.lastBlockedUrl });
const blockMenu = createBlockContextMenu({
    store,
    bridge,
    registry,
    history: paletteHistory,
    surface: null,
    t: host.t,
    chrome,
    host,
});
createSidebarTab();
const drag = createDragMachine(store, bridge, registry, paletteHistory);

const { paletteOpen, keymapOpen, reloadSerial, patchStructure } = useBuilderShell(store, bridge, registry, host.render);

watch(reloadSerial, () => chrome.reload());

// Host-form binding: the store owns state while mounted; every mutation
// (incl. undo/redo) publishes the canvas state up. No back-watch — the
// same one-way ownership v1 has with its in-place model mutations.
watch(store.lastMutation, (mutation) => {
    if (mutation !== null) {
        model.value = store.content.value;
    }
});

// The lite harness document ships an empty marked region — seed it from
// the current form value once the bridge is up (and again after reload).
watch(store.bridgeReady, (ready) => {
    if (ready) {
        void patchStructure();
    }
});

const sidebarTargetExists = ref(true);

onMounted(() => {
    sidebarTargetExists.value = document.querySelector('#sidebar') !== null;
});
</script>

<style scoped>
.pb2--lite {
    display: block;
}

/* Embedded in a host form — a sticky bar would pin inside the fieldset. */
.pb2--lite :deep(.pb2-bar) {
    position: static;
}
</style>
